import { CloseIcon } from '@chakra-ui/icons'
import {
  Flex,
  HStack,
  IconButton,
  Popover,
  PopoverArrow,
  PopoverContent,
  PopoverTrigger,
  Text,
} from '@chakra-ui/react'
import { DateField, getDateString, InputSize } from '@hb/shared'
import { forwardRef, useCallback, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { DayPicker } from 'react-day-picker'
import 'react-day-picker/style.css'
import { InputElement, InputProps } from '../../../../types/fields'
import InputContainer from '../Shared/InputContainer'
import { useInputSize } from '../Shared/sizes'
import { baseInputStyle } from '../Shared/styles'
import { useDefaultToNow } from './hooks'
import { SetToNowButton } from './SetToNowButton'
import './styles.css'

const jan1940 = new Date(1940, 0, 1)
const tenYearsFromNow = new Date(new Date().setFullYear(new Date().getFullYear() + 10))

const MIN_WIDTH: Record<InputSize, string> = {
  xs: '100px',
  sm: '110px',
  md: '120px',
  lg: '140px',
}

export const PopoverDateInput: InputElement<DateField> = forwardRef<
  { focus: () => void },
  InputProps<DateField>
>((props, ref) => {
  const { input, field, disabled, style, readOnly, inputStyle } = props
  const { onChange, value, name, onFocus, onBlur } = input
  const { placeholder, minDate, maxDate, optional, isoFormat, withNowButton } = field

  const [isOpen, setIsOpen] = useState(false)

  useDefaultToNow(field, value, onChange)

  const date = useMemo(() => {
    if (!value) return new Date()
    const d = new Date(value)
    d.setTime(d.getTime() + d.getTimezoneOffset() * 60 * 1000)
    return d
  }, [value])

  const triggerRef = useRef<HTMLDivElement>(null)

  const handleOpen = useCallback(() => {
    if (disabled) return
    setIsOpen(true)
    onFocus()
  }, [onFocus, disabled])

  const handleClose = useCallback(() => {
    setIsOpen(false)
    onBlur()
  }, [onBlur])

  useImperativeHandle(
    ref,
    () => ({
      focus: () => {
        handleOpen()
      },
      blur: () => {
        handleClose()
      },
    }),
    [handleOpen, handleClose],
  )

  const fromDate = useMemo(() => {
    if (!minDate) return jan1940
    if (minDate === 'now') return new Date()
    return new Date(minDate)
  }, [minDate])

  const toDate = useMemo(() => {
    if (!maxDate) return tenYearsFromNow
    if (maxDate === 'now') return new Date()
    return new Date(maxDate)
  }, [maxDate])

  const size = useInputSize(props.size ?? field?.size)

  const placeholderWithOptional = useMemo(() => {
    if (optional) return `${placeholder} (optional)`
    return placeholder
  }, [optional, placeholder])
  const showClearButton = value && !disabled && !readOnly && !!optional
  const popover = (
    <Popover
      isOpen={isOpen}
      initialFocusRef={triggerRef}
      onOpen={handleOpen}
      onClose={handleClose}
      trigger="click"
      strategy="fixed"
      // closeDelay={100}
      placement="top">
      <PopoverTrigger>
        <InputContainer
          ref={triggerRef}
          flexProps={{
            cursor: 'pointer',
            _hover: {
              background: 'gray.50',
            },
            onClick: e => {
              e.stopPropagation()
              e.preventDefault()
              handleOpen()
            },
          }}
          {...props}
          style={{
            minWidth: MIN_WIDTH[size],
            ...style,
          }}>
          <Flex position="relative" pr={showClearButton ? '28px' : '0'} align="center" gap={2}>
            <Text
              style={{
                ...baseInputStyle(size, disabled),
                width: 'auto',
                color: value && !disabled ? '#111' : '#718096',
                ...inputStyle,
              }}>
              {value ? getDateString(value, 'short', true) : placeholderWithOptional}
            </Text>
            {showClearButton ? (
              <IconButton
                position="absolute"
                right="0"
                top="50%"
                transform="translateY(-50%)"
                size="xs"
                width="20px"
                minW="0"
                height="20px"
                bg="gray.100"
                borderRadius="full"
                aria-label="Clear"
                tabIndex={-1}
                icon={<CloseIcon color="gray.500" w={2} h={2} />}
                onClick={e => {
                  e.stopPropagation()
                  onChange(undefined)
                  handleClose()
                }}
              />
            ) : null}
          </Flex>
        </InputContainer>
      </PopoverTrigger>
      {/* <Portal> */}
      <PopoverContent width="auto" zIndex={2} onFocus={onFocus}>
        <PopoverArrow />
        <DayPicker
          required={!optional}
          // disabled={disabled}
          captionLayout="dropdown"
          defaultMonth={date}
          startMonth={fromDate}
          animate
          navLayout="after"
          endMonth={toDate}
          id={name}
          mode="single"
          selected={date}
          // TODO: Ask Katie if we still want date to change when month changes
          onMonthChange={updated => {
            if (!value) return
            const modified = new Date(updated.getTime() + updated.getTimezoneOffset() * 60 * 1000)
            modified.setUTCDate(date.getUTCDate())
            onChange(isoFormat ? modified?.toISOString().split('T')[0] : modified?.getTime())
          }}
          onDayClick={updated => {
            if (isoFormat) {
              onChange(updated?.toISOString().split('T')[0])
            } else {
              onChange(
                updated ? updated.getTime() - updated.getTimezoneOffset() * 60 * 1000 : undefined,
              )
              // onBlur()
            }
          }}
        />
      </PopoverContent>
      {/* </Portal> */}
    </Popover>
  )

  if (!withNowButton) return popover

  return (
    <HStack spacing={2} align="center">
      {popover}
      <SetToNowButton field={field} onChange={onChange} disabled={disabled} readOnly={readOnly} />
    </HStack>
  )
})
